import { Answer, Attachment, Status, Subsection, SurveyResponse } from 'types/surveyResponse';
import { Survey, SurveyQuestion, SurveySection, SurveySubsection, SurveyOption } from 'types/survey';

// sorts all sections and subsections in the survey according to order fields
export const sortSurvey = (surveyDefinition: Survey) => ({
  ...surveyDefinition,
  sections: [...surveyDefinition.sections]
    .sort((s1, s2) => s1.order - s2.order)
    .map((section) => ({
      ...section,
      subsections: [...section.subsections]
        .sort((ss1, ss2) => ss1.order - ss2.order)
        .map((subsection) => ({
          ...subsection,
          questions: [...subsection.questions].sort((q1, q2) => q1.order - q2.order)
        }))
    }))
});

type SubsectionType<T> = T extends Survey ? SurveySubsection : T extends SurveyResponse ? Subsection : never;

export const getSubsection = <T extends Survey | SurveyResponse>(
  from: T,
  sectionId: string,
  subsectionId: string
): SubsectionType<T> | undefined => {
  const section = from.sections.find((s) => s.sectionId === sectionId);
  if (!section) {
    return undefined;
  }
  return section.subsections.find((ss) => ss.subsectionId === subsectionId) as SubsectionType<T>;
};

// creates or updates a subsection in response, recreating object/array hierarchy above it instead of mutating
type QuestionType<T> = T extends Survey ? SurveyQuestion : T extends SurveyResponse ? Answer : never;

const findQuestionInSubsection = <T extends Survey | SurveyResponse>(
  subsection: SurveySubsection | Subsection,
  questionId: string
): QuestionType<T> | undefined => {
  const surveySubsection = subsection as SurveySubsection;
  const subSection = subsection as Subsection;

  const question = surveySubsection.questions?.find((q) => q.questionId === questionId);
  if (question) return question as QuestionType<T>;

  const answer = subSection.answers?.find((a) => a.questionId === questionId);
  if (answer) return answer as QuestionType<T>;

  if (subSection.pages) {
    const pageQuestion = subSection.pages.flatMap((p) => p.answers).find((a) => a && a.questionId === questionId);
    if (pageQuestion) return pageQuestion as QuestionType<T>;
  }
  return undefined;
};

export const getQuestion = <T extends Survey | SurveyResponse>(
  from: T,
  questionId: string
): QuestionType<T> | undefined => {
  for (const section of from.sections) {
    for (const subsection of section.subsections) {
      const found = findQuestionInSubsection<T>(subsection, questionId);
      if (found) return found;
    }
  }
  return undefined;
};

// returns the updated response and the subsection itself
const updateSubsection = (
  surveyResponse: SurveyResponse,
  sectionId: string,
  subsectionId: string
): [SurveyResponse, Subsection] => {
  surveyResponse = { ...surveyResponse };
  let section = surveyResponse.sections.find((s) => s.sectionId === sectionId);
  if (section) {
    section = { ...section };
    surveyResponse.sections = surveyResponse.sections.map((s) => (s.sectionId === sectionId ? section! : s));
  } else {
    section = { sectionId, subsections: [] };
    surveyResponse.sections = [...surveyResponse.sections, section];
  }
  let subsection = section.subsections.find((ss) => ss.subsectionId === subsectionId);
  if (subsection) {
    subsection = { ...subsection };
    section.subsections = section.subsections.map((ss) => (ss.subsectionId === subsectionId ? subsection! : ss));
  } else {
    subsection = {
      subsectionId,
      answers: [],
      status: Status.InProgress,
      pages: [],
      statusHistory: [{ status: Status.InProgress, changedAt: new Date().toISOString() }],
      comment: undefined,
      attachments: []
    };
    section.subsections = [...section.subsections, subsection];
  }
  return [surveyResponse, subsection];
};

// sets an answer in a response, creating section and/or subsection if necessary,
// creating new objects/arrays instead of mutating existing ones
export const updateAnswer = (
  surveyResponse: SurveyResponse,
  sectionId: string,
  subsectionId: string,
  answer: Answer
) => {
  const [newResponse, subsection] = updateSubsection(surveyResponse, sectionId, subsectionId);
  if (subsection.answers.find((a) => a.questionId === answer.questionId)) {
    subsection.answers = subsection.answers.map((a) => (a.questionId === answer.questionId ? answer : a));
  } else {
    subsection.answers = [...subsection.answers, answer];
  }
  return newResponse;
};

export const updatePageAnswers = (
  surveyResponse: SurveyResponse,
  sectionId: string,
  subsectionId: string,
  answers: Answer[],
  page: string,
  pageNumber: number
) => {
  const [newResponse, subsection] = updateSubsection(surveyResponse, sectionId, subsectionId);
  const pageNum = pageNumber ?? subsection?.pages?.length ?? 0;

  if (!subsection.pages?.length) {
    // creating new pages list and adding new page with answers
    subsection.pages = [{ page, answers }];
  } else {
    // mapping answers to the existing page
    subsection.pages = subsection.pages.map((p, index) => {
      if (index === pageNum) {
        return { ...p, answers };
      }

      return p;
    });

    if (pageNum > subsection.pages.length - 1) {
      // append new page, with answers, to the end of existing pages list
      subsection.pages = [...subsection.pages, { page, answers }];
    }
  }

  return newResponse;
};

export const deletePageAnswers = (
  surveyResponse: SurveyResponse,
  sectionId: string,
  subsectionId: string,
  pageNumber: number
) => {
  const [newResponse, subsection] = updateSubsection(surveyResponse, sectionId, subsectionId);
  const newPages = [...(subsection.pages || [])];
  newPages.splice(pageNumber, 1);
  subsection.pages = newPages;
  return newResponse;
};

export const updateSubsectionStatus = (
  surveyDefinition: Survey,
  surveyResponse: SurveyResponse,
  sectionId: string,
  subsectionId: string,
  isSkipped: boolean,
  reason?: string
) => {
  const [newResponse, subsection] = updateSubsection(surveyResponse, sectionId, subsectionId);
  const surveySubsection = getSubsection<Survey>(surveyDefinition, sectionId, subsectionId);

  let newStatus = Status.Skipped;
  if (!isSkipped) {
    // if status is not skipped, determine it from how many questions are answered
    const numAnswers = subsection.answers.filter((a) => a.value !== null).length;
    const numPageAnswers =
      subsection.pages?.reduce((acc, p) => acc + p.answers.filter((a) => a.value !== null).length, 0) ?? 0;
    const totalAnswers = numAnswers + numPageAnswers;
    const numQuestions = surveySubsection?.questions.filter(q => isRequired(q, surveyResponse)).length ?? 0;
    // count only pages that actually exist in survey
    const numPageQuestions = surveySubsection?.pages
      ?.filter(sdp => subsection.pages?.some(srp => srp.page === sdp.page))
      .reduce((acc, p) => acc + p.questions.filter(q => isRequired(q, surveyResponse)).length, 0) ?? 0;
    const totalQuestions = numQuestions + numPageQuestions;
    if (totalAnswers === 0) {
      newStatus = Status.NotStarted;
    } else if (totalAnswers >= totalQuestions) {
      newStatus = Status.Complete;
    } else {
      newStatus = Status.InProgress;
    }
  }

  if (subsection.status !== newStatus) {
    subsection.status = newStatus;
    subsection.statusHistory = [
      ...subsection.statusHistory,
      { status: newStatus, changedAt: new Date().toISOString() }
    ];
    subsection.comment = reason;
    return newResponse;
  } else if (subsection.comment !== reason) {
    subsection.comment = reason;
    return newResponse;
  }

  // status not changed, no need to update anything
  return surveyResponse;
};

export const updateSubsectionAttachments = (
  surveyResponse: SurveyResponse,
  sectionId: string,
  subsectionId: string,
  attachments: Attachment[]
) => {
  const [newResponse, subsection] = updateSubsection(surveyResponse, sectionId, subsectionId);
  subsection.attachments = attachments;
  return newResponse;
};

export const getProductToPageMap = (surveyQuestions: SurveyQuestion[]): Record<string, string> => {
  const mapping: Record<string, string> = {};

  for (const question of surveyQuestions) {
    if (question.name === 'products_in_use' && question.options) {
      for (const option of question.options) {
        const valueSlug = option.name; // machine-readable
        const prettyValue = option.value; // human-readable
        mapping[valueSlug] = prettyValue;
      }
    }
  }

  return mapping;
};

export const getAttachmentsOfType = (attachments: Attachment[] | undefined, type: string | string[]) => {
  if (typeof type === 'string') {
    type = [type];
  }
  return (attachments || []).filter((a) => type.filter((t) => a.mimeType.startsWith(t)).length);
};

export const updateAttachmentsOfType = (
  attachments: Attachment[] | undefined,
  type: string | string[],
  updated: Attachment[]
) => {
  if (typeof type === 'string') {
    type = [type];
  }
  return [...(attachments || []).filter((a) => !type.filter((t) => a.mimeType.startsWith(t)).length), ...updated];
};

export const isVisible = (what: SurveySection | SurveySubsection | SurveyQuestion, surveyResponse: SurveyResponse) => {
  if (!what.dependsOn) {
    return true;
  }

  let dependency: Answer | undefined;
  if (Array.isArray(what.dependsOn)) {
    // AND logic: all dependencies must be satisfied
    const dependencies = what.dependsOn.map((dep) => getQuestion(surveyResponse, dep.questionId));
    // If any dependency is missing, not visible
    if (dependencies.some((dep) => !dep)) {
      return false;
    }
    // All dependencies must match their values
    return what.dependsOn.every((dep, idx) => {
      const depAnswer = dependencies[idx];
      if (!depAnswer) return false;
      if (Array.isArray(dep.value)) {
        if (Array.isArray(depAnswer.value)) {
          // dep.value is array, depAnswer.value is array: any overlap
          return dep.value.some((v) =>
            Array.isArray(depAnswer.value) ? depAnswer.value.includes(v) : depAnswer.value === v
          );
        } else {
          // dep.value is array, depAnswer.value is string or number: any match
          return depAnswer.value !== null && dep.value.some((val) => val === depAnswer.value);
        }
      } else {
        return Array.isArray(depAnswer.value) ? depAnswer.value.includes(dep.value) : depAnswer.value === dep.value;
      }
    });
  } else {
    dependency = getQuestion(surveyResponse, what.dependsOn.questionId);
  }
  if (!dependency) {
    return false;
  }
  
  if (Array.isArray(dependency.value)) {
    return Array.isArray(what.dependsOn.value)
      ? what.dependsOn.value.some(v => (dependency.value as string[]).includes(v)) : (dependency.value as string[]).includes(what.dependsOn.value);
  }
  
  return Array.isArray(what.dependsOn.value) ? what.dependsOn.value.includes(dependency.value?.toString() ?? "") : dependency.value === what.dependsOn.value;
};

export const trimInvisibleAnswers = (survey: Survey, surveyResponse: SurveyResponse): SurveyResponse => {
  return {
    ...surveyResponse,
    sections: surveyResponse.sections.map((s) => ({
      ...s,
      subsections: s.subsections.map((ss) => ({
        ...ss,
        answers: ss.answers.filter((a) => {
          const question = getQuestion(survey, a.questionId);
          if (!question) {
            return false;
          }
          return isVisible(question, surveyResponse);
        })
      }))
    }))
  };
};

export const isRequired = (what: SurveyQuestion, surveyResponse: SurveyResponse) => {
  if (!isVisible(what, surveyResponse)) {
    return false;
  }
  
  // todo: handle the actual "required" field, survey definitions don't really use it yet
  
  // these types are never required
  if (what.type === 'attachment_only' || what.type === 'call-to-action') {
    return false;
  }
  
  // masked by products page
  if (what.name === 'products_in_use') {
    return false;
  }
  
  // choice question with no actual options (should not happen but apparently does at least in coll. repair survey now)
  if (what.type === 'choice' && !what.options?.length) {
    return false;
  }

  return true;
};


export const filterDependent = (what: SurveyQuestion | SurveyOption, siblingAnswers: Answer[]) => {
  if (!what.dependsOn) {
    return true;
  }

  // Normalize dependsOn to array
  const dependsOnArr = Array.isArray(what.dependsOn) ? what.dependsOn : [what.dependsOn];

  // All dependencies (AND logic)
  return dependsOnArr.every((dep) => {
    const depAnswer = siblingAnswers.find((a) => a.questionId === dep.questionId);
    if (!depAnswer) {
      return false;
    }
    if (Array.isArray(dep.value)) {
      // OR logic: at least one value in dep.value must match depAnswer.value
      if (Array.isArray(depAnswer.value) && depAnswer.value !== null) {
        return dep.value.some((v) => {
          if (Array.isArray(depAnswer.value)) {
            return depAnswer.value.includes(v);
          } else if (typeof depAnswer.value === 'string' || typeof depAnswer.value === 'number') {
            return depAnswer.value === v;
          }
          return false;
        });
      } else if (
        (typeof depAnswer.value === 'string' || typeof depAnswer.value === 'number') &&
        depAnswer.value !== null
      ) {
        return dep.value.includes(String(depAnswer.value));
      } else {
        return false;
      }
    } else {
      // Single value: must match
      return Array.isArray(depAnswer.value) && depAnswer.value !== null
        ? depAnswer.value.map(String).includes(String(dep.value))
        : depAnswer.value !== null && depAnswer.value === dep.value;
    }
  });
};

// comparison function from npm version-compare package, slightly reworded
// https://github.com/bevry/version-compare/blob/master/source/index.ts
const surveyDefVersionCompare = (v1: Survey, v2: Survey) => {
  const v1p = String(v1.version).split('.'), v2p = String(v2.version).split('.');
  for (let depth = 0; depth < Math.min(v1p.length, v2p.length); depth++) {
    const v1n = Number(v1p[depth]), v2n = Number(v2p[depth]);
    if (v1n > v2n) return 1;
    if (v2n > v1n) return -1;
    if (!Number.isNaN(v1n) && Number.isNaN(v2n)) return 1;
    if (Number.isNaN(v1n) && !Number.isNaN(v2n)) return -1;
  }
  return 0;
};

// filters survey definitions so that only the latest version of each one is presented
// (grouping by name), and sorts by localized name
export const getAvailableSortedSurveyDefs = (surveyDefinitions: Survey[], locale: string | undefined) =>
  Object.values(Object.groupBy(surveyDefinitions, sd => sd.name))
    .map(sds => sds!.toSorted(surveyDefVersionCompare))
    .map(sds => sds.at(-1)!)
    .toSorted((s1, s2) => {
      const name1 = locale && s1.localizations[locale] ? s1.localizations[locale][s1.name] ?? s1.name : s1.name;
      const name2 = locale && s2.localizations[locale] ? s2.localizations[locale][s2.name] ?? s2.name : s2.name;
      if (name1 > name2) return 1;
      if (name1 < name2) return -1;
      return 0;
    });